import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Save, Trash2, Plus, ChevronUp, ChevronDown,
  Lock, Play, CheckCircle2, User, Mail, Rocket, ExternalLink, Check,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminCheck, adminCBGet, adminCBUpdate, adminCBAddPhase, adminCBUpdatePhase,
  adminCBDeletePhase, adminCBReorder,
} from "../lib/api";
import { AdminNav } from "./AdminContinueBuilding";

const SUB_STATUSES = ["active", "paused", "cancelled", "past_due"];

export default function AdminContinueBuildingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [data, setData] = useState(null);
  const [proj, setProj] = useState({});
  const [savingProj, setSavingProj] = useState(false);

  const load = useCallback(async () => {
    const res = await adminCBGet(id);
    setData(res);
    const p = res.project;
    setProj({
      project_name: p.project_name || "",
      subscription_status: p.subscription_status || "active",
      monthly_price: p.monthly_price ?? 149,
      renewal_date: p.renewal_date || "",
    });
  }, [id]);

  useEffect(() => {
    adminCheck()
      .then((r) => {
        if (!r.admin) { setAuthorized(false); navigate("/"); return; }
        setAuthorized(true);
        load().catch(() => navigate("/admin/continue-building"));
      })
      .catch(() => { setAuthorized(false); navigate("/"); });
  }, [navigate, load]);

  const saveProject = async () => {
    setSavingProj(true);
    try {
      await adminCBUpdate(id, { ...proj, monthly_price: Number(proj.monthly_price) });
      toast.success("Subscription updated");
      await load();
    } catch { toast.error("Update failed"); }
    finally { setSavingProj(false); }
  };

  const phases = data ? [...(data.project.phases || [])].sort((a, b) => a.order - b.order) : [];

  const setPhaseStatus = async (phaseId, status) => {
    try { const p = await adminCBUpdatePhase(id, phaseId, { status }); setData((d) => ({ ...d, project: p })); toast.success(`Phase ${status}`); }
    catch { toast.error("Failed"); }
  };
  const savePhase = async (phaseId, payload) => {
    try { const p = await adminCBUpdatePhase(id, phaseId, payload); setData((d) => ({ ...d, project: p })); toast.success("Phase saved"); }
    catch { toast.error("Save failed"); }
  };
  const deletePhase = async (phaseId) => {
    try { const p = await adminCBDeletePhase(id, phaseId); setData((d) => ({ ...d, project: p })); toast.success("Phase removed"); }
    catch { toast.error("Delete failed"); }
  };
  const move = async (index, dir) => {
    const ids = phases.map((p) => p.id);
    const ni = index + dir;
    if (ni < 0 || ni >= ids.length) return;
    [ids[index], ids[ni]] = [ids[ni], ids[index]];
    try { const p = await adminCBReorder(id, ids); setData((d) => ({ ...d, project: p })); }
    catch { toast.error("Reorder failed"); }
  };
  const addPhase = async () => {
    try { const p = await adminCBAddPhase(id, { name: "New Phase", description: "", includes: [] }); setData((d) => ({ ...d, project: p })); toast.success("Phase added"); }
    catch { toast.error("Failed"); }
  };

  if (authorized === null || !data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]"><Loader2 className="animate-spin text-[#D4AF37]" size={30} /></div>;
  }

  const { project, customer } = data;

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-cb-detail">
      <header className="border-b border-white/10 bg-[#0B0B0B]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-4">
          <button onClick={() => navigate("/admin/continue-building")} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors" data-testid="admin-cb-back">
            <ArrowLeft size={16} /> All build programs
          </button>
          <AdminNav active="cb" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold">{project.project_name}</h1>
            <p className="text-sm text-[#A1A1AA] mt-1 flex items-center gap-4">
              <span className="inline-flex items-center gap-1.5"><User size={14} /> {customer?.name || "—"}</span>
              <span className="inline-flex items-center gap-1.5"><Mail size={14} /> {customer?.email || project.customer_email}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#71717A]">Overall progress</p>
            <p className="font-display text-2xl font-bold text-[#D4AF37]">{project.progress_percentage || 0}%</p>
          </div>
        </div>

        {/* Subscription controls */}
        <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-8" data-testid="admin-cb-sub-controls">
          <h2 className="font-display font-semibold text-lg mb-4">Subscription</h2>
          <div className="grid sm:grid-cols-4 gap-4">
            <div><label className="block text-xs text-[#71717A] mb-1.5">Project name</label>
              <input className="admin-input" value={proj.project_name} onChange={(e) => setProj({ ...proj, project_name: e.target.value })} data-testid="cb-field-name" /></div>
            <div><label className="block text-xs text-[#71717A] mb-1.5">Status</label>
              <select className="admin-input capitalize [&>option]:text-black" value={proj.subscription_status} onChange={(e) => setProj({ ...proj, subscription_status: e.target.value })} data-testid="cb-field-status">
                {SUB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div><label className="block text-xs text-[#71717A] mb-1.5">Monthly price ($)</label>
              <input type="number" className="admin-input" value={proj.monthly_price} onChange={(e) => setProj({ ...proj, monthly_price: e.target.value })} data-testid="cb-field-price" /></div>
            <div><label className="block text-xs text-[#71717A] mb-1.5">Renewal date</label>
              <input type="date" className="admin-input [color-scheme:dark]" value={proj.renewal_date} onChange={(e) => setProj({ ...proj, renewal_date: e.target.value })} data-testid="cb-field-renewal" /></div>
          </div>
          <button onClick={saveProject} disabled={savingProj} className="mt-4 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-50" data-testid="cb-save-sub-btn">
            {savingProj ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save subscription
          </button>
        </section>

        <DeploymentSummary deployment={project.deployment} />

        {/* Phases */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-semibold text-lg">Build Phases ({phases.length})</h2>
          <button onClick={addPhase} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5 transition-colors" data-testid="cb-add-phase-btn"><Plus size={15} /> Add phase</button>
        </div>

        <div className="space-y-4">
          {phases.map((p, i) => (
            <PhaseEditor
              key={p.id}
              phase={p}
              index={i}
              count={phases.length}
              onStatus={setPhaseStatus}
              onSave={savePhase}
              onDelete={deletePhase}
              onMove={move}
            />
          ))}
        </div>
      </main>
    </div>
  );
}

const DEPLOY_LABELS = {
  web_app: "Web App", shareable_link: "Shareable Link", qr_code: "QR Code",
  social_media: "Social Media", pwa: "PWA Setup", google_play: "Google Play", apple_app_store: "Apple App Store",
};
const DEPLOY_STATUS_STYLE = {
  draft: "bg-white/10 text-[#A1A1AA]", ready: "bg-amber-500/15 text-amber-400", deployed: "bg-emerald-500/15 text-emerald-400",
};

const DeploymentSummary = ({ deployment }) => {
  const dep = deployment || {};
  const types = dep.types || [];
  const checklist = dep.checklist || [];
  const status = dep.status || "draft";
  const hasData = types.length > 0 || dep.live_url || checklist.length > 0 || dep.updated_at;

  return (
    <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-8" data-testid="admin-cb-deployment">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-display font-semibold text-lg inline-flex items-center gap-2"><Rocket size={18} className="text-[#D4AF37]" /> Deployment</h2>
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${DEPLOY_STATUS_STYLE[status]}`} data-testid="admin-cb-deploy-status">{status}</span>
      </div>

      {!hasData ? (
        <p className="text-sm text-[#71717A]">The customer hasn't set up deployment yet.</p>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-xs text-[#71717A] mb-1.5">Live app URL</p>
            {dep.live_url ? (
              /^https?:\/\//i.test(dep.live_url) ? (
                <a href={dep.live_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#D4AF37] hover:underline break-all" data-testid="admin-cb-deploy-url">
                  {dep.live_url} <ExternalLink size={13} className="shrink-0" />
                </a>
              ) : (
                <span className="text-sm text-[#A1A1AA] break-all" data-testid="admin-cb-deploy-url">{dep.live_url}</span>
              )
            ) : <p className="text-sm text-[#A1A1AA]">—</p>}
          </div>

          <div>
            <p className="text-xs text-[#71717A] mb-2">Chosen deployment types</p>
            {types.length ? (
              <div className="flex flex-wrap gap-1.5" data-testid="admin-cb-deploy-types">
                {types.map((t) => (
                  <span key={t} className="text-xs bg-[#D4AF37]/12 text-[#D4AF37] rounded-full px-2.5 py-1">{DEPLOY_LABELS[t] || t}</span>
                ))}
              </div>
            ) : <p className="text-sm text-[#A1A1AA]">—</p>}
          </div>

          {checklist.length > 0 && (
            <div>
              <p className="text-xs text-[#71717A] mb-2">Store readiness checklist</p>
              <ul className="space-y-1">
                {checklist.map((c, i) => (
                  <li key={i} className="text-sm text-[#D4D4D8] flex items-center gap-2"><Check size={13} className="text-emerald-400" /> {c}</li>
                ))}
              </ul>
            </div>
          )}

          {dep.updated_at && (
            <p className="text-xs text-[#71717A]">Last updated {new Date(dep.updated_at).toLocaleString()}</p>
          )}
        </div>
      )}
    </section>
  );
};

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

const PhaseEditor = ({ phase, index, count, onStatus, onSave, onDelete, onMove }) => {
  const [open, setOpen] = useState(phase.status === "current");
  const [f, setF] = useState({
    name: phase.name || "",
    description: phase.description || "",
    includes: toLines(phase.includes),
    builder_prompt: phase.builder_prompt || "",
    documentation_url: phase.documentation_url || "",
    project_files_url: phase.project_files_url || "",
    video_url: phase.video_url || "",
    blueprint: phase.blueprint || "",
    checklist: toLines(phase.checklist),
    next_steps: phase.next_steps || "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = () => onSave(phase.id, {
    name: f.name,
    description: f.description,
    includes: fromLines(f.includes),
    builder_prompt: f.builder_prompt,
    documentation_url: f.documentation_url,
    project_files_url: f.project_files_url,
    video_url: f.video_url,
    blueprint: f.blueprint,
    checklist: fromLines(f.checklist),
    next_steps: f.next_steps,
  });

  const badge = {
    completed: "bg-emerald-500/15 text-emerald-400",
    current: "bg-[#D4AF37]/15 text-[#D4AF37]",
    locked: "bg-white/10 text-[#71717A]",
  }[phase.status];

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden" data-testid={`admin-cb-phase-${index + 1}`}>
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="flex flex-col">
          <button onClick={() => onMove(index, -1)} disabled={index === 0} className="text-[#71717A] hover:text-white disabled:opacity-20"><ChevronUp size={14} /></button>
          <button onClick={() => onMove(index, 1)} disabled={index === count - 1} className="text-[#71717A] hover:text-white disabled:opacity-20"><ChevronDown size={14} /></button>
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setOpen((o) => !o)}>
          <p className="font-medium truncate">Phase {index + 1}: {phase.name}</p>
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase mt-1 ${badge}`}>{phase.status}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button title="Lock" onClick={() => onStatus(phase.id, "locked")} className="p-1.5 rounded-lg hover:bg-white/10 text-[#71717A] hover:text-white" data-testid={`cb-lock-${index + 1}`}><Lock size={15} /></button>
          <button title="Set current (unlock)" onClick={() => onStatus(phase.id, "current")} className="p-1.5 rounded-lg hover:bg-white/10 text-[#D4AF37]" data-testid={`cb-unlock-${index + 1}`}><Play size={15} /></button>
          <button title="Mark complete" onClick={() => onStatus(phase.id, "completed")} className="p-1.5 rounded-lg hover:bg-white/10 text-emerald-400" data-testid={`cb-complete-${index + 1}`}><CheckCircle2 size={15} /></button>
          <button title="Delete phase" onClick={() => onDelete(phase.id)} className="p-1.5 rounded-lg hover:bg-white/10 text-red-400" data-testid={`cb-delete-${index + 1}`}><Trash2 size={15} /></button>
        </div>
      </div>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-white/10 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-xs text-[#71717A] mb-1.5">Phase name</label><input className="admin-input" value={f.name} onChange={set("name")} data-testid={`cb-phase-name-${index + 1}`} /></div>
            <div><label className="block text-xs text-[#71717A] mb-1.5">Documentation URL</label><input className="admin-input" value={f.documentation_url} onChange={set("documentation_url")} placeholder="https://..." /></div>
          </div>
          <div><label className="block text-xs text-[#71717A] mb-1.5">Description</label><input className="admin-input" value={f.description} onChange={set("description")} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-xs text-[#71717A] mb-1.5">Includes (one per line)</label><textarea rows={4} className="admin-input resize-none" value={f.includes} onChange={set("includes")} /></div>
            <div><label className="block text-xs text-[#71717A] mb-1.5">Build checklist (one per line)</label><textarea rows={4} className="admin-input resize-none" value={f.checklist} onChange={set("checklist")} /></div>
          </div>
          <div><label className="block text-xs text-[#71717A] mb-1.5">AI Builder Prompt</label><textarea rows={4} className="admin-input resize-none" value={f.builder_prompt} onChange={set("builder_prompt")} data-testid={`cb-phase-prompt-${index + 1}`} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-xs text-[#71717A] mb-1.5">Project files URL</label><input className="admin-input" value={f.project_files_url} onChange={set("project_files_url")} placeholder="https://..." /></div>
            <div><label className="block text-xs text-[#71717A] mb-1.5">Video URL</label><input className="admin-input" value={f.video_url} onChange={set("video_url")} placeholder="https://..." /></div>
          </div>
          <div><label className="block text-xs text-[#71717A] mb-1.5">Next steps</label><input className="admin-input" value={f.next_steps} onChange={set("next_steps")} /></div>
          <button onClick={save} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2 text-sm font-semibold hover:bg-[#c5a030] transition-colors" data-testid={`cb-save-phase-${index + 1}`}>
            <Save size={15} /> Save phase
          </button>
        </div>
      )}
    </div>
  );
};
